// Founder's Circle share modal.
// Global "Share Culinaire Specialist" pill in the top right of every
// dashboard page. Opens a V2 True Glass modal with the Launch Share Kit:
// master caption, three tailored platform variants, referral progress
// bar, and the Founder's Circle unlock state. Zero em dashes anywhere.

import { useEffect, useState } from "react";
import { Share2, X, Copy, Check, Linkedin, Instagram, Facebook, Sparkles, Trophy, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

function GlassPanel({ children, className = "", radius = 26 }) {
  return (
    <div
      className={`relative ${className}`}
      style={{
        borderRadius: radius,
        background:
          "linear-gradient(180deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.04) 45%, rgba(255,255,255,0.015) 100%)",
        backdropFilter: "blur(44px) saturate(200%)",
        WebkitBackdropFilter: "blur(44px) saturate(200%)",
        border: "1px solid rgba(232,207,154,0.45)",
        boxShadow: [
          "inset 0 1px 0 rgba(255,255,255,0.32)",
          "inset 0 -1px 0 rgba(255,255,255,0.04)",
          "0 60px 140px -30px rgba(0,0,0,0.92)",
          "0 30px 60px -14px rgba(0,0,0,0.7)",
          "0 0 90px -20px rgba(232,207,154,0.35)",
        ].join(","),
      }}
    >
      <div
        aria-hidden
        className="absolute top-0 left-6 right-6 h-px rounded-full pointer-events-none"
        style={{
          background:
            "linear-gradient(90deg, transparent, rgba(232,207,154,0.9), rgba(255,255,255,0.6), rgba(90,178,201,0.85), transparent)",
        }}
      />
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          borderRadius: radius,
          background:
            "linear-gradient(135deg, rgba(255,255,255,0.14) 0%, transparent 32%, transparent 70%, rgba(255,255,255,0.05) 100%)",
          mixBlendMode: "screen",
          opacity: 0.65,
        }}
      />
      <div className="relative z-10">{children}</div>
    </div>
  );
}

const TABS = [
  { id: "master", label: "Master", Icon: Sparkles },
  { id: "linkedin", label: "LinkedIn", Icon: Linkedin },
  { id: "instagram", label: "Instagram", Icon: Instagram },
  { id: "facebook", label: "Facebook", Icon: Facebook },
];

// Small inline formatter. Handles **bold** and _italic_ within a single
// text run. Leaves everything else as plain text.
function renderInlineRich(text) {
  const parts = text.split(/(\*\*[^*]+\*\*|_[^_\n]+_)/g);
  return parts.map((p, i) => {
    if (p.startsWith("**") && p.endsWith("**")) {
      return <strong key={i} className="font-semibold text-white">{p.slice(2, -2)}</strong>;
    }
    if (p.startsWith("_") && p.endsWith("_") && p.length > 2) {
      return <em key={i} className="italic text-white/90">{p.slice(1, -1)}</em>;
    }
    return <span key={i}>{p}</span>;
  });
}

// Full caption renderer. Splits on blank lines to form paragraphs.
// Contiguous bullet lines (starting with the bullet character) become a
// proper list. Everything else renders as a paragraph.
function RenderedCaption({ text }) {
  const blocks = [];
  const paragraphs = text.split(/\n{2,}/);
  paragraphs.forEach((para, idx) => {
    const lines = para.split("\n");
    const bulletLines = [];
    const otherLines = [];
    let mode = null;
    lines.forEach((ln) => {
      if (ln.startsWith("\u2022 ")) {
        if (mode === "other" && otherLines.length) {
          blocks.push({ type: "p", key: `p-${idx}-${blocks.length}`, lines: otherLines.slice() });
          otherLines.length = 0;
        }
        bulletLines.push(ln.slice(2));
        mode = "bullets";
      } else {
        if (mode === "bullets" && bulletLines.length) {
          blocks.push({ type: "ul", key: `u-${idx}-${blocks.length}`, items: bulletLines.slice() });
          bulletLines.length = 0;
        }
        otherLines.push(ln);
        mode = "other";
      }
    });
    if (bulletLines.length) blocks.push({ type: "ul", key: `u-${idx}-${blocks.length}`, items: bulletLines.slice() });
    if (otherLines.length) blocks.push({ type: "p", key: `p-${idx}-${blocks.length}`, lines: otherLines.slice() });
  });

  return (
    <div className="space-y-3.5">
      {blocks.map((b) => {
        if (b.type === "ul") {
          return (
            <ul key={b.key} className="space-y-2 pl-1">
              {b.items.map((it, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <span
                    className="inline-block w-1 h-1 rounded-full mt-2 shrink-0"
                    style={{ background: "#e8cf9a" }}
                  />
                  <span className="text-white/85">{renderInlineRich(it)}</span>
                </li>
              ))}
            </ul>
          );
        }
        return (
          <p key={b.key} className="text-white/85 leading-[1.7]">
            {b.lines.map((ln, i) => (
              <span key={i}>
                {renderInlineRich(ln)}
                {i < b.lines.length - 1 && <br />}
              </span>
            ))}
          </p>
        );
      })}
    </div>
  );
}

function CaptionBlock({ text, testId }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      // Strip markdown wrappers for the clipboard payload so recipients
      // get clean readable text they can paste into any social composer.
      const plain = text
        .replace(/\*\*([^*]+)\*\*/g, "$1")
        .replace(/_([^_\n]+)_/g, "$1");
      await navigator.clipboard.writeText(plain);
      setCopied(true);
      toast.success("Copied to clipboard.");
      setTimeout(() => setCopied(false), 2200);
    } catch {
      toast.error("Copy failed. Select and copy manually.");
    }
  };
  return (
    <div className="relative" data-testid={testId}>
      <div
        className="rounded-xl p-5 text-[13.5px] max-h-[420px] overflow-y-auto"
        style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(232,207,154,0.20)" }}
      >
        <RenderedCaption text={text} />
      </div>
      <button
        onClick={copy}
        className="absolute top-2 right-2 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[10px] uppercase tracking-[0.18em]"
        style={{
          background: copied ? "rgba(141,211,164,0.18)" : "linear-gradient(180deg, rgba(232,207,154,0.32), rgba(200,169,106,0.10))",
          border: copied ? "1px solid rgba(141,211,164,0.55)" : "1px solid rgba(232,207,154,0.55)",
          color: copied ? "#a3e5b8" : "#faf3df",
        }}
        data-testid={`${testId}-copy`}
      >
        {copied ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
      </button>
    </div>
  );
}

function ProgressBar({ count, threshold, unlocked }) {
  const pct = Math.min(100, Math.round((count / threshold) * 100));
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[11px] uppercase tracking-[0.18em] text-white/55">
          Founder's Circle Progress
        </div>
        <div className="text-[11px] uppercase tracking-[0.18em]" style={{ color: unlocked ? "#a3e5b8" : "#e8cf9a" }}>
          {count}/{threshold} referrals
        </div>
      </div>
      <div
        className="h-2 rounded-full overflow-hidden"
        style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(232,207,154,0.20)" }}
      >
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{
            width: `${pct}%`,
            background: unlocked
              ? "linear-gradient(90deg, rgba(141,211,164,0.9), rgba(90,178,201,0.85))"
              : "linear-gradient(90deg, rgba(232,207,154,0.9), rgba(200,169,106,0.55))",
            boxShadow: "0 0 12px rgba(232,207,154,0.35)",
          }}
        />
      </div>
      <div className="text-[11px] text-white/50 mt-1.5">
        {unlocked
          ? "Founder's Circle unlocked. Lifetime 20 percent discount is live on your account."
          : `${threshold - count} more successful sign ups unlocks lifetime 20 percent off and the Founder's Circle badge.`}
      </div>
    </div>
  );
}

export function ShareLauncher() {
  // The share entry point now lives inside the sidebar (desktop) and the
  // mobile navigation drawer (see Layout.jsx). This launcher stays as a
  // no-op so the modal keeps its self-mounted pattern without also
  // rendering a floating pill that overlaps the dashboard.
  return null;
}

export default function ShareModal() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("master");

  // Listen for the global open event so mobile header buttons and any
  // other launcher can open the same modal without lifting state.
  useEffect(() => {
    const openIt = () => setOpen(true);
    window.addEventListener("cs:open-share", openIt);
    return () => window.removeEventListener("cs:open-share", openIt);
  }, []);

  useEffect(() => {
    if (!open || data) return;
    (async () => {
      try {
        const r = await api.get("/founders/status");
        setData(r.data);
      } catch {
        toast.error("Could not load the share kit.");
      }
    })();
  }, [open, data]);

  if (!open) return null;

  const unlocked = !!data?.founders_circle;
  const caption = (data?.captions || {})[tab] || "";

  return (
    <>
      <div
        className="fixed inset-0 z-[80] flex items-center justify-center px-4 py-8"
        style={{ background: "rgba(4,3,2,0.72)", backdropFilter: "blur(6px)" }}
        data-testid="share-modal"
      >
        <GlassPanel className="w-full max-w-[600px] p-6 md:p-8 max-h-[92vh] overflow-y-auto">
          <button
            onClick={() => setOpen(false)}
            className="absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center"
            style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.10)" }}
            data-testid="share-modal-close"
          >
            <X className="w-4 h-4 text-white/70" />
          </button>

          <div
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] uppercase tracking-[0.22em] mb-4 font-semibold"
            style={{
              background: unlocked ? "rgba(141,211,164,0.10)" : "rgba(212,175,55,0.14)",
              border: unlocked ? "1px solid rgba(141,211,164,0.35)" : "1px solid rgba(212,175,55,0.45)",
              color: unlocked ? "#a3e5b8" : "#D4AF37",
            }}
          >
            {unlocked ? <><Trophy className="w-3 h-3" /> Founder's Circle</> : <><Sparkles className="w-3 h-3" /> Founding Member Share</>}
          </div>

          <h2
            className="font-display leading-[1.05] mb-3"
            style={{ fontSize: "2.25rem", fontWeight: 800, letterSpacing: "-0.02em" }}
            data-testid="share-modal-title"
          >
            {unlocked ? "Welcome to the Founder's Circle." : "Share the standard of precision."}
          </h2>
          <p className="text-[0.95rem] text-white/78 leading-relaxed mb-6" data-testid="share-modal-subtitle">
            {unlocked
              ? "You brought three operators onto the platform. Your lifetime 20 percent discount is now permanent on this account. Keep sharing to help us raise the industry."
              : "Every kitchen you invite lands with a fourteen day free trial. Bring three operators onto the platform and unlock lifetime 20 percent off, plus the Founder's Circle badge."}
          </p>

          {data && (
            <div className="mb-6">
              <ProgressBar count={data.referrals_count} threshold={data.threshold} unlocked={unlocked} />
            </div>
          )}

          {/* Pending Referrals list. Shows every credited but not yet
              matured referral so the founder sees exactly who is still
              on the 30 day activity clock. Fraud prevention: the timer
              only starts once the referred user has priced at least one
              recipe. */}
          {data?.pending?.length > 0 && (
            <div
              className="mb-5 rounded-xl p-3"
              style={{
                background: "rgba(212,175,55,0.06)",
                border: "1px solid rgba(212,175,55,0.28)",
              }}
              data-testid="share-pending-list"
            >
              <div className="label-eyebrow gold mb-2" style={{ fontSize: "0.65rem" }}>
                Active maturity
              </div>
              <ul className="space-y-1.5">
                {data.pending.map((p, i) => (
                  <li
                    key={i}
                    className="flex items-center justify-between gap-3 text-xs"
                    data-testid={`share-pending-row-${i}`}
                  >
                    <span className="truncate text-white/85">{p.referred_email || "Pending kitchen"}</span>
                    <span
                      className="shrink-0 px-2 py-0.5 rounded-full font-medium"
                      style={{
                        background: p.activity_started ? "rgba(212,175,55,0.14)" : "rgba(255,255,255,0.05)",
                        border: p.activity_started ? "1px solid rgba(212,175,55,0.4)" : "1px solid rgba(255,255,255,0.10)",
                        color: p.activity_started ? "#f5e6b0" : "rgba(255,255,255,0.65)",
                      }}
                    >
                      {p.status_label}
                    </span>
                  </li>
                ))}
              </ul>
              <div className="text-[0.65rem] text-white/50 mt-2 leading-relaxed">
                Referrals count toward the Founder's Circle after 30 consecutive active days. The clock starts only once a referred kitchen has costed a real recipe.
              </div>
            </div>
          )}

          {/* Tabs */}
          <div
            className="flex items-center gap-1 mb-4 p-1 rounded-full"
            style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(232,207,154,0.22)" }}
            data-testid="share-caption-tabs"
          >
            {TABS.map(({ id, label, Icon }) => (
              <button
                key={id}
                onClick={() => setTab(id)}
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full text-[11px] uppercase tracking-[0.16em] transition-colors"
                style={
                  tab === id
                    ? {
                        background: "linear-gradient(180deg, rgba(232,207,154,0.32), rgba(200,169,106,0.10))",
                        border: "1px solid rgba(232,207,154,0.5)",
                        color: "#faf3df",
                      }
                    : { color: "rgba(255,255,255,0.55)" }
                }
                data-testid={`share-tab-${id}`}
              >
                <Icon className="w-3 h-3" />
                {label}
              </button>
            ))}
          </div>

          {/* Caption */}
          {data ? (
            <CaptionBlock text={caption} testId={`share-caption-${tab}`} />
          ) : (
            <div className="h-32 rounded-xl animate-pulse" style={{ background: "rgba(255,255,255,0.03)" }} />
          )}

          {/* Share URL + native share */}
          {data && (
            <div className="mt-5 flex items-center gap-2">
              <div
                className="flex-1 min-w-0 rounded-xl px-3 py-2 text-xs text-white/70 truncate"
                style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(232,207,154,0.22)" }}
                data-testid="share-url"
                title={data.share_url}
              >
                {data.share_url}
              </div>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(data.share_url);
                  toast.success("Referral link copied.");
                }}
                className="shrink-0 inline-flex items-center gap-1 px-3 py-2 rounded-xl text-[10px] uppercase tracking-[0.16em]"
                style={{
                  background: "linear-gradient(180deg, rgba(232,207,154,0.32), rgba(200,169,106,0.10))",
                  border: "1px solid rgba(232,207,154,0.55)",
                  color: "#faf3df",
                }}
                data-testid="share-url-copy"
              >
                <Copy className="w-3 h-3" /> Copy link
              </button>
              {typeof navigator !== "undefined" && navigator.share && (
                <button
                  onClick={async () => {
                    try {
                      await navigator.share({
                        title: "Culinaire Specialist",
                        text: caption,
                        url: data.share_url,
                      });
                    } catch {
                      /* silent */
                    }
                  }}
                  className="shrink-0 inline-flex items-center gap-1 px-3 py-2 rounded-xl text-[10px] uppercase tracking-[0.16em]"
                  style={{
                    background: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(232,207,154,0.32)",
                    color: "#faf3df",
                  }}
                  data-testid="share-native"
                >
                  <ArrowRight className="w-3 h-3" /> Share
                </button>
              )}
            </div>
          )}
        </GlassPanel>
      </div>
    </>
  );
}
